"use client";

import { type PersonaDraft, personaDisplayLabels } from "@repo/core/client";
import { Button } from "@ui/components/button";
import {
	HoverCard,
	HoverCardContent,
	HoverCardTrigger,
} from "@ui/components/hover-card";
import { PersonaCard } from "@ui/components/persona-card";
import { InfoIcon, SparklesIcon, UsersIcon } from "lucide-react";
import { useEffect, useMemo, useRef } from "react";

export const PERSONA_PANEL_ANCHOR = "personas";

/**
 * The persona panel next to the prompt tabs: cards from the shared
 * PersonaCard (marketing variant; people and people at organizations), the first preview open. Fires
 * `onViewed` once, the first time the panel scrolls into view.
 */
export function PersonaPanel({
	personas,
	segments,
	onViewed,
}: {
	personas: readonly PersonaDraft[];
	segments: readonly string[];
	onViewed?: () => void;
}) {
	const ref = useRef<HTMLElement>(null);
	const labels = useMemo(() => personaDisplayLabels(personas), [personas]);
	const onViewedRef = useRef(onViewed);
	onViewedRef.current = onViewed;

	useEffect(() => {
		const node = ref.current;
		if (!node) {
			return;
		}
		if (typeof IntersectionObserver === "undefined") {
			onViewedRef.current?.();
			return;
		}
		const observer = new IntersectionObserver(
			(entries) => {
				if (entries.some((entry) => entry.isIntersecting)) {
					onViewedRef.current?.();
					observer.disconnect();
				}
			},
			{ threshold: 0.25 },
		);
		observer.observe(node);
		return () => observer.disconnect();
	}, []);

	return (
		<section
			ref={ref}
			id={PERSONA_PANEL_ANCHOR}
			aria-labelledby="personas-heading"
			className="scroll-mt-28 space-y-3"
		>
			<div className="flex flex-col gap-1 sm:flex-row sm:items-start sm:justify-between sm:gap-3">
				<div className="min-w-0">
					<h3
						id="personas-heading"
						className="flex items-center gap-2 text-base font-semibold"
					>
						<UsersIcon
							className="size-4 text-primary"
							aria-hidden
						/>
						Your personas
					</h3>
					<p className="mt-0.5 text-sm text-muted-foreground">
						{personas.length === 1
							? "1 person"
							: `${personas.length} people`}{" "}
						who'd ask AI about your category, drafted from your
						site.
					</p>
				</div>
				<HoverCard openDelay={100}>
					<HoverCardTrigger asChild>
						<button
							type="button"
							className="-ml-1.5 inline-flex shrink-0 items-center gap-1 self-start rounded-md px-1.5 py-1 text-xs font-medium text-muted-foreground hover:bg-muted hover:text-foreground sm:ml-0"
						>
							<InfoIcon className="size-3.5" aria-hidden />
							How we built these
						</button>
					</HoverCardTrigger>
					<HoverCardContent
						align="end"
						className="w-80 space-y-2 text-sm"
					>
						<p>
							{segments.length > 0
								? `We read your site and found ${segments.length} audience segment${segments.length === 1 ? "" : "s"}: ${segments.join(", ")}.`
								: "We read your site to see who you serve."}
						</p>
						<p className="text-muted-foreground">
							Then we drafted different people who'd ask these
							questions: people asking for themselves, people
							asking for their organization, or both, depending on
							who asks about you.
						</p>
						<p className="text-muted-foreground">
							Open a card to see the line we'd put in front of a
							prompt to ask as that person. The question stays the
							same; only the asker changes.
						</p>
					</HoverCardContent>
				</HoverCard>
			</div>
			<ul className="space-y-3">
				{personas.map((persona, i) => (
					<li
						key={`${labels[i]}-${persona.contextText.slice(0, 24)}`}
					>
						<PersonaCard
							persona={{ ...persona, label: labels[i] }}
							variant="marketing"
							defaultPreviewOpen={i === 0}
						/>
					</li>
				))}
			</ul>
		</section>
	);
}

/**
 * A result without personas (switched off, or drafting failed): a gentle
 * way back to the form with the option on. No extra endpoint: it simply
 * generates again with personas.
 */
export function AddPersonasCard({
	segments,
	onAdd,
}: {
	segments: readonly string[];
	onAdd: () => void;
}) {
	return (
		<section className="space-y-4 rounded-xl border border-dashed p-5">
			<div className="flex items-center gap-2">
				<UsersIcon className="size-4 text-primary" aria-hidden />
				<h3 className="text-base font-semibold">See who's asking</h3>
			</div>
			<p className="text-sm text-muted-foreground">
				{segments.length > 0
					? `Add 3 to 5 personas drafted from the ${segments.length} audience segment${segments.length === 1 ? "" : "s"} we found on your site. Your prompts stay exactly as they are.`
					: "Add 3 to 5 personas drafted from who your site serves. Your prompts stay exactly as they are."}
			</p>
			<div className="grid gap-2" aria-hidden>
				{["w-2/3", "w-1/2"].map((w) => (
					<div
						key={w}
						className="flex items-center gap-3 rounded-lg border border-dashed p-3"
					>
						<span className="size-8 shrink-0 rounded-full bg-muted" />
						<div className="flex-1 space-y-1.5">
							<span
								className={`block h-2.5 rounded bg-muted ${w}`}
							/>
							<span className="block h-2 w-1/3 rounded bg-muted" />
						</div>
					</div>
				))}
			</div>
			<div className="space-y-1.5">
				<Button variant="outline" onClick={onAdd}>
					<SparklesIcon className="size-4" />
					Add personas
				</Button>
				<p className="text-xs text-muted-foreground">
					Free, like the rest of your results.
				</p>
			</div>
		</section>
	);
}
